import { useState, useRef, useEffect } from 'react'
import ReactMarkdown from 'react-markdown'
import rehypeRaw from 'rehype-raw'
import supabase, { getActiveSession, checkSupabaseConnection, getUserSubscription as getSubscription } from '../utils/supabaseClient'
import Link from 'next/link'
import ProfileButton from '../components/ProfileButton'
import { useRouter } from 'next/router'
import CreditWarningBanner from '../components/CreditWarningBanner'

// Event untuk modal upgrade dan memberitahu perubahan kredit
// Inisialisasi event secara lazy di dalam useEffect untuk memastikan hanya dijalankan di browser
let showUpgradeModalEvent;
let creditUpdatedEvent;

export default function Home() {
  const [text, setText] = useState('')
  const [articleType, setArticleType] = useState('SEO')
  const [language, setLanguage] = useState('Bahasa Indonesia')
  const [writingStyle, setWritingStyle] = useState('Resmi')
  const [result, setResult] = useState('')
  const [timestamp, setTimestamp] = useState('')
  const [loading, setLoading] = useState(false)
  const [copySuccess, setCopySuccess] = useState(false)
  const [streaming, setStreaming] = useState(false)
  const [saveStatus, setSaveStatus] = useState('') // Status penyimpanan artikel
  const [referenceSource, setReferenceSource] = useState('Indonesia') // State untuk sumber referensi
  const [humanizing, setHumanizing] = useState(false) // State untuk status humanize
  const [humanized, setHumanized] = useState(false) // State untuk teks yang sudah dihumanize
  const [userSubscription, setUserSubscription] = useState(null) // State untuk info langganan pengguna
  const [loadingSubscription, setLoadingSubscription] = useState(true) // State untuk loading info langganan
  const [user, setUser] = useState(null) // Tambahkan state user untuk identifikasi pengguna
  
  // State untuk dropdown custom
  const [typeDropdownOpen, setTypeDropdownOpen] = useState(false)
  const [languageDropdownOpen, setLanguageDropdownOpen] = useState(false)
  const [styleDropdownOpen, setStyleDropdownOpen] = useState(false)
  const [referenceSourceDropdownOpen, setReferenceSourceDropdownOpen] = useState(false) // State untuk dropdown sumber referensi
  
  const resultRef = useRef(null)
  const eventSourceRef = useRef(null)
  const typeDropdownRef = useRef(null)
  const languageDropdownRef = useRef(null)
  const styleDropdownRef = useRef(null)
  const referenceSourceDropdownRef = useRef(null) // Ref untuk dropdown sumber referensi

  const router = useRouter();

  // Tambahkan state untuk menampilkan banner
  const [showCreditWarning, setShowCreditWarning] = useState(false)
  const [creditWarningMessage, setCreditWarningMessage] = useState('')

  // Menyembunyikan loading overlay saat halaman dimuat
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const loadingOverlay = document.querySelector('.article-loading-overlay');
      if (loadingOverlay) {
        loadingOverlay.classList.remove('active');
        loadingOverlay.classList.add('article-loading-exit');
        setTimeout(() => {
          loadingOverlay.classList.remove('article-loading-exit');
        }, 800);
      }
    }
  }, []);

  // Menutup dropdown saat klik di luar
  useEffect(() => {
    function handleClickOutside(event) {
      if (typeDropdownRef.current && !typeDropdownRef.current.contains(event.target)) {
        setTypeDropdownOpen(false)
      }
      if (languageDropdownRef.current && !languageDropdownRef.current.contains(event.target)) {
        setLanguageDropdownOpen(false)
      }
      if (styleDropdownRef.current && !styleDropdownRef.current.contains(event.target)) {
        setStyleDropdownOpen(false)
      }
      if (referenceSourceDropdownRef.current && !referenceSourceDropdownRef.current.contains(event.target)) {
        setReferenceSourceDropdownOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  // Membersihkan event source pada unmount
  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close()
      }
    }
  }, [])

  // Mendapatkan user session saat komponen dimuat
  useEffect(() => {
    const getUserSession = async () => {
      try {
        const session = await getActiveSession();
        if (session?.user) {
          setUser(session.user);
        }
      } catch (error) {
        console.error('Error getting user session:', error);
      }
    };

    getUserSession();
  }, []);

  // Effect untuk mengubah sumber referensi secara otomatis berdasarkan bahasa
  useEffect(() => {
    if (language !== 'Bahasa Indonesia') {
      setReferenceSource('International');
    }
  }, [language]);

  // Event listener untuk me-refresh halaman saat pengguna kembali ke tab
  useEffect(() => {
    // Ganti dengan versi yang lebih aman yang hanya memuat ulang data
    // tanpa reload seluruh halaman
    const refreshDataOnly = () => {
      // Hanya refresh data jika dokumen terlihat dan tidak ada loading lain yang berjalan
      if (document.visibilityState === 'visible' && !loadingSubscription) {
        console.log('Memperbarui data saat tab aktif...');
        
        // Batasi pembaruan data dengan cek waktu terakhir refresh
        const lastRefresh = localStorage.getItem('lastDataRefreshTime');
        const now = Date.now();
        
        // Jika belum pernah refresh atau sudah lebih dari 10 menit
        if (!lastRefresh || (now - parseInt(lastRefresh)) > 10 * 60 * 1000) {
          // Simpan waktu refresh
          localStorage.setItem('lastDataRefreshTime', now.toString());
          // Panggil getUserSubscription langsung tanpa cek user
          getUserSubscription();
        } else {
          console.log('Memperbarui data diabaikan, refresh terakhir kurang dari 10 menit yang lalu');
        }
      }
    };
    
    // Gunakan interval untuk memeriksa data, bukan reload halaman
    // Perbesar interval ke 10 menit untuk mengurangi beban server
    const dataCheckInterval = setInterval(refreshDataOnly, 10 * 60 * 1000); // 10 menit
    
    // Juga pasang event listener untuk visibility change
    // agar data dapat di-refresh saat tab kembali aktif setelah tidak aktif lama
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Tambahkan delay saat tab menjadi aktif untuk menghindari lonjakan permintaan
        setTimeout(refreshDataOnly, 3000);
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      clearInterval(dataCheckInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Inisialisasi CustomEvent di browser saja
  useEffect(() => {
    if (typeof window !== 'undefined') {
      showUpgradeModalEvent = new CustomEvent('showUpgradeModal');
      creditUpdatedEvent = new CustomEvent('creditUpdated');
    }
  }, []);

  // Tambahkan fungsi untuk memeriksa kredit pengguna
  const checkUserCredit = async (creditNeeded = 1) => {
    try {
      // Periksa status koneksi terlebih dahulu
      const isConnected = await checkSupabaseConnection();
      if (!isConnected) {
        return { 
          success: false, 
          error: 'Tidak dapat terhubung ke server. Periksa koneksi internet Anda.',
          isConnectionError: true
        };
      }
      
      // Ambil data sesi pengguna dengan fungsi yang lebih robust
      const session = await getActiveSession();
      if (!session) {
        return { success: false, error: 'Anda harus login untuk menggunakan fitur ini' };
      }
      
      // Gunakan getSubscription yang telah dioptimalkan
      const { data, error } = await getSubscription(session.user.id);
      
      if (error) {
        console.error('Error checking subscription:', error);
        return { success: false, error: 'Tidak ada langganan aktif. Silakan beli paket terlebih dahulu.' };
      }
      
      if (!data) {
        return { success: false, error: 'Anda belum memiliki paket. Silakan beli paket terlebih dahulu.' };
      }
      
      // Periksa masa berlaku langganan
      if (new Date(data.subscription_end) < new Date()) {
        // Nonaktifkan langganan yang sudah berakhir
        await supabase
          .from('user_subscriptions')
          .update({ is_active: false })
          .eq('id', data.id);
        
        return { success: false, error: 'Langganan Anda telah berakhir. Silakan perpanjang langganan Anda.' };
      }
      
      // Periksa jumlah kredit yang tersisa
      if (data.credits_remaining < creditNeeded) {
        // Jika kredit benar-benar habis (0 atau kurang), tampilkan banner
        if (data.credits_remaining <= 0) {
          // Pesan spesifik untuk paket Free
          if (data.plan_name === 'FREE') {
            return { 
              success: false, 
              error: 'Kredit gratis Anda telah habis. Silakan upgrade ke paket berbayar untuk mendapatkan lebih banyak kredit.', 
              isFreePackage: true 
            };
          }
          return { success: false, error: 'Kredit Anda telah habis. Silakan isi ulang kredit Anda.' };
        } 
        // Jika kredit ada tapi tidak cukup, berikan pesan tanpa menampilkan banner
        else {
          return { 
            success: false, 
            error: `Kredit tidak cukup. Anda membutuhkan ${creditNeeded} kredit, tapi hanya memiliki ${data.credits_remaining} kredit.`,
            showBanner: false
          };
        }
      }
      
      // Pengguna memiliki kredit yang cukup
      console.log(`Pengguna memiliki kredit yang cukup: ${data.credits_remaining} kredit`);
      return { success: true, subscription: data };
    } catch (error) {
      console.error('Error checking user credit:', error);
      return { success: false, error: 'Terjadi kesalahan saat memeriksa kredit.' };
    }
  };

  // Fungsi untuk memperbarui (mengurangi) kredit pengguna
  const decreaseUserCredit = async (subscriptionData) => {
    try {
      if (!subscriptionData || !subscriptionData.id) {
        console.error('Data langganan tidak valid untuk memperbarui kredit');
        return false;
      }
      
      // Kurangi kredit yang tersisa
      const newCreditsRemaining = subscriptionData.credits_remaining - 1;
      
      // Update data langganan di Supabase
      const { error } = await supabase
        .from('user_subscriptions')
        .update({ 
          credits_remaining: newCreditsRemaining 
        })
        .eq('id', subscriptionData.id);
      
      if (error) {
        console.error('Error mengurangi kredit pengguna:', error);
        return false;
      }
      
      console.log('Berhasil mengurangi kredit. Sisa kredit:', newCreditsRemaining);
      
      // Refresh data langganan di state lokal dengan membuat objek baru
      // JANGAN memanggil getUserSubscription() karena akan menyebabkan loop fetching
      setUserSubscription(prev => {
        if (!prev) return null;
        return {...prev, credits_remaining: newCreditsRemaining};
      });
      
      // Pancarkan event bahwa kredit telah diperbarui untuk memperbarui tampilan di ProfileButton
      // Tambahkan throttling untuk mencegah terlalu banyak event dalam waktu singkat
      if (typeof window !== 'undefined') {
        window.lastCreditEventTime = window.lastCreditEventTime || 0;
        const now = Date.now();
        
        // Jika sudah memancarkan event dalam 3 detik terakhir, skip
        if (now - window.lastCreditEventTime < 3000) {
          console.log('Batasi event kredit, terlalu sering dalam waktu singkat');
        } else {
          window.lastCreditEventTime = now;
          if (creditUpdatedEvent) {
            document.dispatchEvent(creditUpdatedEvent);
          }
        }
      }
      
      return true;
    } catch (error) {
      console.error('Error dalam fungsi decreaseUserCredit:', error);
      return false;
    }
  };

  // Memanusiakan teks artikel
  const humanizeText = async () => {
    try {
      if (!result) return;
      
      // Periksa kredit pengguna
      const creditCheck = await checkUserCredit(1);
      console.log('Index.js: Hasil creditCheck:', creditCheck);
      
      if (!creditCheck.success) {
        // Jika error koneksi, tampilkan pesan error koneksi
        if (creditCheck.isConnectionError) {
          setCreditWarningMessage(creditCheck.error);
          setShowCreditWarning(true);
          console.log('Banner error koneksi ditampilkan dalam humanizeText:', creditCheck.error);
          return;
        }
        
        if (creditCheck.isFreePackage) {
          // Tampilkan banner kredit habis daripada modal di ProfileButton
          console.log('Index.js: Menampilkan banner - Free Package');
          const creditMsg = 'Kredit gratis Anda tidak cukup. Silakan upgrade ke paket berbayar untuk mendapatkan lebih banyak kredit.';
          setCreditWarningMessage(creditMsg);
          setShowCreditWarning(true);
          console.log('Index.js: Menampilkan banner kredit habis dengan pesan:', creditMsg);
        } else {
          // Tampilkan banner untuk semua jenis pesan error kredit
          console.log('Index.js: Menampilkan banner - Error:', creditCheck.error);
          setCreditWarningMessage(creditCheck.error);
          setShowCreditWarning(true);
        }
        return;
      }
      
      // Simpan data subscription untuk digunakan nanti
      const subscriptionData = creditCheck.subscription;
      
      setHumanizing(true)
      setHumanized(false)
      
      // Tampilkan loading overlay
      const loadingOverlay = document.querySelector('.article-loading-overlay');
      if (loadingOverlay) {
        loadingOverlay.classList.add('active');
        
        // Perbarui teks loading
        const loadingTitle = document.querySelector('.article-loading-title');
        if (loadingTitle) loadingTitle.textContent = 'Memanusiakan Artikel';
        
        const loadingText = document.querySelector('.article-loading-text');
        if (loadingText) loadingText.textContent = 'Sedang memodifikasi teks agar terlihat alami dan lolos dari deteksi AI. Proses ini membutuhkan waktu beberapa saat...';
        
        const loadingStatus = document.querySelector('.article-loading-status');
        if (loadingStatus) loadingStatus.textContent = 'Membuat variasi kalimat dan gaya penulisan...';
      }
      
      // Ekstrak bagian referensi sebelum mengirim ke API humanisasi
      let textContent = result;
      let referenceSection = "";
      
      // Cek jika artikel memiliki bagian referensi
      const referencePatterns = [
        /\n\n(References|REFERENCES|Referensi|REFERENSI|Daftar Pustaka|DAFTAR PUSTAKA)[\s\S]*$/i,
        /\n\n(Bibliography|BIBLIOGRAPHY|Bibliografi|BIBLIOGRAFI)[\s\S]*$/i,
        /\n\n(Citations|CITATIONS|Sitasi|SITASI)[\s\S]*$/i,
        /\n\n(## References|## REFERENCES|## Referensi|## REFERENSI|## Daftar Pustaka|## DAFTAR PUSTAKA)[\s\S]*$/i,
        /\n\n(## Bibliography|## BIBLIOGRAPHY|## Bibliografi|## BIBLIOGRAFI)[\s\S]*$/i,
        /\n\n(## Citations|## CITATIONS|## Sitasi|## SITASI)[\s\S]*$/i,
        /\n\n(## Pustaka|## PUSTAKA|## Sumber|## SUMBER)[\s\S]*$/i,
        /\n\n(# References|# REFERENCES|# Referensi|# REFERENSI|# Daftar Pustaka|# DAFTAR PUSTAKA)[\s\S]*$/i,
        /\n(<div class=["']reference-list["'][\s\S]*$)/i,
        /\n(<div class=['"]reference-list['"][\s\S]*$)/i,
        /\n\n(Sumber Referensi|SUMBER REFERENSI)[\s\S]*$/i,
        /\n\n(Sumber|SUMBER)[\s\S]*$/i,
        /\n\n([0-9]+\.\s+https?:\/\/)[\s\S]*$/i, // Deteksi daftar nomor dengan URL
        /\n\n(\[\d+\]\s+)[\s\S]*$/i // Deteksi referensi format [1] Judul
      ];
      
      // Cari pola referensi
      for (const pattern of referencePatterns) {
        const match = result.match(pattern);
        if (match) {
          referenceSection = match[0];
          textContent = result.replace(referenceSection, ''); // Hapus bagian referensi dari konten
          console.log('Bagian referensi ditemukan dan dipisahkan untuk dipertahankan');
          break;
        }
      }
      
      // Kirim konten artikel (tanpa referensi) ke API humanisasi
      const response = await fetch('/api/humanize-text', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text: textContent
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.error || `Error: ${response.status} ${response.statusText}`;
        throw new Error(errorMessage);
      }
      
      const data = await response.json();
      
      // Gabungkan kembali konten yang dihumanisasi dengan bagian referensi
      const finalHumanizedText = referenceSection ? data.humanizedText + referenceSection : data.humanizedText;
      
      // Perbarui konten artikel dengan teks yang dihumanisasi
      setResult(finalHumanizedText);
      setHumanized(true);
      
      // Kurangi kredit pengguna setelah berhasil menghumanisasi teks
      decreaseUserCredit(subscriptionData);
      
      // Sembunyikan animasi loading
      if (loadingOverlay) {
        loadingOverlay.classList.add('article-loading-exit');
        setTimeout(() => {
          loadingOverlay.classList.remove('active', 'article-loading-exit');
        }, 800);
      }
      
      // Tampilkan notifikasi sukses
      setTimeout(() => {
        const editorContainer = document.querySelector('.editor-container');
        if (editorContainer) {
          const successNotif = document.createElement('div');
          successNotif.className = 'humanize-success-notification';
          successNotif.innerHTML = '✓ Artikel berhasil dihumanisasi!';
          editorContainer.prepend(successNotif);
          
          // Hapus notifikasi setelah beberapa detik
          setTimeout(() => {
            successNotif.classList.add('fade-out');
            setTimeout(() => successNotif.remove(), 500);
          }, 3000);
        }
      }, 800);
      
    } catch (error) {
      console.error('Error humanizing text:', error);
      
      // Sembunyikan animasi loading jika terjadi error
      const loadingOverlay = document.querySelector('.article-loading-overlay');
      if (loadingOverlay) {
        loadingOverlay.classList.add('article-loading-exit');
        setTimeout(() => {
          loadingOverlay.classList.remove('active', 'article-loading-exit');
        }, 800);
      }
      
      // Tampilkan pesan error yang bermakna
      const errorMessage = error.message || 'Terjadi kesalahan saat memanusiakan artikel';
      alert(errorMessage);
    } finally {
      setHumanizing(false)
    }
  };

  // Fungsi untuk menyimpan artikel ke Supabase
  const saveArticleToSupabase = async () => {
    if (!result || !text) {
      alert('Artikel kosong, tidak dapat menyimpan')
      return
    }

    setSaveStatus('saving')
    
    try {
      // Ambil user ID dari sesi dengan cara sederhana - gunakan metode fetch API untuk menghindari masalah dengan library
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) {
        throw new Error('Anda harus login untuk menyimpan artikel')
      }
      
      // Batasi ukuran artikel jika terlalu besar - kecilkan lagi menjadi 50KB
      let trimmedContent = result
      if (trimmedContent.length > 50000) {
        trimmedContent = trimmedContent.substring(0, 50000) + "\n\n[Artikel terpotong karena terlalu besar]"
      }
      
      const userId = session.user.id
      const articleData = { 
        title: text.substring(0, 200),
        content: trimmedContent,
        article_type: articleType,
        language: language,
        writing_style: writingStyle,
        created_at: timestamp || new Date().toISOString(),
        is_humanized: humanized,
        user_id: userId
      }
      
      console.log('Mencoba menyimpan artikel dengan fetch API langsung...')
      
      // Gunakan fetch API langsung daripada library Supabase untuk menghindari masalah throttling
      const apiUrl = `${process.env.NEXT_PUBLIC_SUPABASE_URL}/rest/v1/articles`
      const apiKey = supabase.supabaseKey
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': apiKey,
          'Authorization': `Bearer ${apiKey}`,
          'Prefer': 'return=minimal'
        },
        body: JSON.stringify([articleData])
      })
      
      if (!response.ok) {
        const errorData = await response.text()
        console.error('Error response:', response.status, errorData)
        throw new Error(`Error ${response.status}: ${errorData || response.statusText}`)
      }
      
      console.log('Artikel berhasil disimpan dengan fetch API')
      setSaveStatus('success')
      setTimeout(() => setSaveStatus(''), 2000)
    } catch (error) {
      console.error('Error saving article:', error)
      
      // Tampilkan pesan error yang spesifik
      let errorMessage = 'Gagal menyimpan artikel'
      
      if (error.message && (error.message.includes('network') || error.message.includes('Failed to fetch'))) {
        errorMessage = 'Gagal menyimpan artikel: Masalah koneksi jaringan'
      } else if (error.message && (error.message.includes('auth') || error.message.includes('session'))) {
        errorMessage = 'Gagal menyimpan artikel: Silakan login ulang'
      } else if (error.message && error.message.includes('413')) {
        errorMessage = 'Gagal menyimpan artikel: Artikel terlalu besar'
      } else if (error.message && error.message.includes('429')) {
        errorMessage = 'Gagal menyimpan artikel: Terlalu banyak request. Coba lagi nanti'
      } else {
        errorMessage = `Gagal menyimpan artikel: ${error.message}`
      }
      
      alert(errorMessage)
      setSaveStatus('error')
      setTimeout(() => setSaveStatus(''), 2000)
    } finally {
      // Pastikan status tidak stuck di saving
      if (saveStatus === 'saving') {
        setTimeout(() => {
          setSaveStatus('')
        }, 500)
      }
    }
  }

  // Fungsi untuk memuat artikel dari Supabase
  const loadArticles = async () => {
    try {
      // Ambil user ID dari sesi
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) {
        throw new Error('Anda harus login untuk memuat artikel')
      }
      
      const { data, error } = await supabase
        .from('articles')
        .select('*')
        .eq('user_id', session.user.id) // Filter berdasarkan user_id
        .order('created_at', { ascending: false })
        .limit(10)
      
      if (error) throw error
      
      return data
    } catch (error) {
      console.error('Error loading articles:', error)
      return []
    }
  }

  // Modifikasi fungsi handleSubmit
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Periksa kredit pengguna
    const creditCheck = await checkUserCredit(1);
    if (!creditCheck.success) {
      console.log('Kredit check gagal:', creditCheck);
      
      // Jika error koneksi, tampilkan pesan error koneksi
      if (creditCheck.isConnectionError) {
        setCreditWarningMessage(creditCheck.error);
        setShowCreditWarning(true);
        console.log('Banner error koneksi ditampilkan:', creditCheck.error);
        return;
      }
      
      // Jika showBanner = false, beri pesan tanpa menampilkan banner
      if (creditCheck.showBanner === false) {
        alert(creditCheck.error);
        return;
      }
      
      // Jika paket gratis habis, tampilkan pesan upgrade
      if (creditCheck.isFreePackage) {
        // Tampilkan banner kredit habis, bukan menampilkan modal di ProfileButton
        const creditMsg = 'Kredit gratis Anda telah habis. Silakan upgrade ke paket berbayar untuk mendapatkan lebih banyak kredit.';
        setCreditWarningMessage(creditMsg);
        setShowCreditWarning(true);
        console.log('Banner kredit habis seharusnya muncul - Free Package dengan pesan:', creditMsg);
      } else {
        // Tampilkan banner untuk semua jenis pesan error kredit
        setCreditWarningMessage(creditCheck.error);
        setShowCreditWarning(true);
        console.log('Banner kredit habis seharusnya muncul - Error:', creditCheck.error);
      }
      return;
    }
    
    // Simpan data subscription untuk digunakan nanti
    const subscriptionData = creditCheck.subscription;
    
    setResult('');
    setLoading(true);
    setStreaming(true);
    setSaveStatus('');

    // Tutup semua dropdown
    setTypeDropdownOpen(false);
    setLanguageDropdownOpen(false);
    setStyleDropdownOpen(false);
    setReferenceSourceDropdownOpen(false);

    // Tampilkan animasi loading
    const loadingOverlay = document.querySelector('.article-loading-overlay');
    if (loadingOverlay) {
      loadingOverlay.classList.add('active');
    }

    // Tutup EventSource sebelumnya jika ada
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    try {
      // Menyiapkan parameter untuk API
      // Pastikan referenceSource memiliki nilai yang tepat sebelum dimasukkan ke params
      const actualReferenceSource = articleType !== 'Cerita' ? referenceSource : 'Indonesia';
      
      // Buat URL untuk EventSource dengan parameter
      const params = new URLSearchParams({ 
        text, 
        articleType, 
        language, 
        writingStyle
      });
      
      // Tambahkan referenceSource secara eksplisit jika bukan artikel Cerita
      if (articleType !== 'Cerita') {
        params.append('referenceSource', actualReferenceSource);
      }
      
      // Log untuk debugging
      console.log(`Data yang dikirim ke server:`, {
        text,
        articleType,
        language,
        writingStyle,
        referenceSource: actualReferenceSource
      });
      
      const eventSource = new EventSource(`/api/generate-stream?${params}`);
      eventSourceRef.current = eventSource;

      let receivedText = '';
      let hasReceivedFirstChunk = false;

      // Menangani pesan yang masuk
      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.error) {
            // Pastikan pesan error adalah string, bukan objek
            const errorMessage = typeof data.error === 'object' ? 
              JSON.stringify(data.error) : 
              data.error;
              
            setResult(`Error: ${errorMessage}`);
            eventSource.close();
            setStreaming(false);
            hideLoadingAnimation(); // Sembunyikan animasi loading
            return;
          }

          // Ignore web search status messages
          if (data.searchStatus) {
            // Do nothing, we're not showing the search animation
            return;
          }

          if (data.chunk) {
            if (!hasReceivedFirstChunk) {
              hasReceivedFirstChunk = true;
              hideLoadingAnimation(); // Sembunyikan animasi saat chunk pertama diterima
            }
            receivedText += data.chunk;
            setResult(receivedText);
          }

          if (data.timestamp && !timestamp) {
            setTimestamp(data.timestamp);
          }

          if (data.done) {
            eventSource.close();
            setStreaming(false);
            hideLoadingAnimation(); // Pastikan animasi tersembunyi
            
            // Kurangi kredit pengguna setelah berhasil membuat artikel
            decreaseUserCredit(subscriptionData);
          }
        } catch (e) {
          console.error('Error parsing event data:', e, event.data);
          setResult(`Error: Gagal memproses data dari server. Detail: ${e.message}`);
          eventSource.close();
          setStreaming(false);
          hideLoadingAnimation();
        }
      };

      // Menangani error
      eventSource.onerror = (error) => {
        console.error('EventSource error:', error);
        
        // Mengekstrak pesan error yang bermakna
        let errorMessage = 'Gagal menerima data dari server';
        if (error) {
          if (typeof error === 'object') {
            // Mencoba mendapatkan pesan error yang spesifik
            errorMessage = error.message || error.toString() || errorMessage;
          } else if (typeof error === 'string') {
            errorMessage = error;
          }
        }
        
        eventSource.close();
        setStreaming(false);
        hideLoadingAnimation(); // Sembunyikan animasi
        
        if (receivedText === '') {
          setResult(`Error: ${errorMessage}`);
        }
      };
    } catch (error) {
      console.error('Error:', error);
      
      // Ekstrak pesan error yang bermakna
      let errorMessage = 'Terjadi kesalahan saat menghubungi server';
      if (error) {
        if (typeof error === 'object') {
          errorMessage = error.message || error.toString() || errorMessage;
        } else if (typeof error === 'string') {
          errorMessage = error;
        }
      }
      
      setResult(`Error: ${errorMessage}`);
      setStreaming(false);
      hideLoadingAnimation(); // Sembunyikan animasi
    } finally {
      setLoading(false);
    }
  };

  // Fungsi untuk menyembunyikan animasi loading
  const hideLoadingAnimation = () => {
    const overlay = document.querySelector('.article-loading-overlay')
    if (overlay) {
      overlay.classList.add('article-loading-exit')
      setTimeout(() => {
        overlay.classList.remove('active', 'article-loading-exit')
      }, 800)
    }
  }

  const handleCopy = () => {
    if (resultRef.current) {
      // Mengambil HTML dari hasil render markdown
      const contentHTML = resultRef.current.innerHTML;
      
      // Membuat elemen temporer untuk menyimpan HTML
      const tempElement = document.createElement('div');
      tempElement.innerHTML = contentHTML;
      
      // Membuat objek blob dari HTML
      const blob = new Blob([
        '<html><head><meta charset="utf-8"><title>Artikel</title></head><body>',
        tempElement.innerHTML,
        '</body></html>'
      ], { type: 'text/html' });
      
      try {
        // Menggunakan Clipboard API modern dengan format HTML
        const clipboardItem = new ClipboardItem({
          'text/html': blob,
          'text/plain': new Blob([tempElement.innerText], { type: 'text/plain' })
        });
        
        navigator.clipboard.write([clipboardItem])
          .then(() => {
            setCopySuccess(true)
            setTimeout(() => setCopySuccess(false), 2000)
          })
          .catch(err => {
            console.error('Gagal menyalin dengan format rich text:', err);
            // Fallback ke metode sederhana jika gagal
            navigator.clipboard.writeText(tempElement.innerText)
              .then(() => {
                setCopySuccess(true)
                setTimeout(() => setCopySuccess(false), 2000)
              })
              .catch(() => alert('Gagal menyalin teks!'))
          });
      } catch (e) {
        // Fallback untuk browser yang tidak mendukung ClipboardItem
        navigator.clipboard.writeText(tempElement.innerText)
          .then(() => {
            setCopySuccess(true)
            setTimeout(() => setCopySuccess(false), 2000)
          })
          .catch(() => alert('Gagal menyalin teks!'))
      }
    }
  }

  const handleCreateNew = () => {
    setText('')
    setResult('')
    setTimestamp('')
    setSaveStatus('')
    setHumanized(false) // Reset status humanized
    // Tetap mempertahankan jenis artikel yang sama
  }

  // Format timestamp ke format yang lebih mudah dibaca
  const formatTimestamp = (isoString) => {
    if (!isoString) return ''
    const date = new Date(isoString)
    return date.toLocaleString('id-ID', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  // Fungsi untuk render konten artikel termasuk HTML di dalamnya
  const renderArticleContent = () => {
    // Cek apakah hasil berisi tag HTML untuk referensi
    if (result.includes('<div class="reference-list">') || result.includes('<div class=\'reference-list\'>')) {
      const parts = result.split('## Referensi');
      
      if (parts.length === 2) {
        const [articleContent, referenceContent] = parts;
        
        return (
          <>
            <ReactMarkdown
              rehypePlugins={[rehypeRaw]}
              components={{
                a: ({node, ...props}) => <a target="_blank" rel="noopener noreferrer" {...props} />
              }}
            >
              {articleContent}
            </ReactMarkdown>
            
            <h2>Referensi</h2>
            <div dangerouslySetInnerHTML={{ __html: referenceContent.trim() }} />
          </>
        );
      }
    }
    
    // Jika tidak ada tag HTML khusus, gunakan ReactMarkdown biasa
    return (
      <ReactMarkdown
        rehypePlugins={[rehypeRaw]}
        components={{
          a: ({node, ...props}) => <a target="_blank" rel="noopener noreferrer" {...props} />
        }}
      >
        {result}
      </ReactMarkdown>
    );
  };

  // Data untuk dropdown
  const articleTypes = [
    { value: 'SEO', label: 'Artikel SEO' },
    { value: 'Faktual', label: 'Artikel Faktual' },
    { value: 'Ilmiah', label: 'Artikel Ilmiah' },
    { value: 'LongForm', label: 'Artikel Long Form' },
    { value: 'Berita', label: 'Artikel Berita' },
    { value: 'Cerita', label: 'Artikel Cerita' }
  ]

  const languages = [
    { value: 'Bahasa Indonesia', label: 'Bahasa Indonesia' },
    { value: 'Arabic', label: 'Arabic' },
    { value: 'Bengali', label: 'Bengali' },
    { value: 'Bulgarian', label: 'Bulgarian' },
    { value: 'Chinese', label: 'Chinese (Simplified / Traditional)' },
    { value: 'Croatian', label: 'Croatian' },
    { value: 'Czech', label: 'Czech' },
    { value: 'Danish', label: 'Danish' },
    { value: 'Dutch', label: 'Dutch' },
    { value: 'English', label: 'English' },
    { value: 'Estonian', label: 'Estonian' },
    { value: 'Farsi', label: 'Farsi' },
    { value: 'Finnish', label: 'Finnish' },
    { value: 'French', label: 'French' },
    { value: 'German', label: 'German' },
    { value: 'Greek', label: 'Greek' },
    { value: 'Gujarati', label: 'Gujarati' },
    { value: 'Hebrew', label: 'Hebrew' },
    { value: 'Hindi', label: 'Hindi' },
    { value: 'Hungarian', label: 'Hungarian' },
    { value: 'Italian', label: 'Italian' },
    { value: 'Japanese', label: 'Japanese' },
    { value: 'Kannada', label: 'Kannada' },
    { value: 'Korean', label: 'Korean' },
    { value: 'Latvian', label: 'Latvian' },
    { value: 'Lithuanian', label: 'Lithuanian' },
    { value: 'Malayalam', label: 'Malayalam' },
    { value: 'Marathi', label: 'Marathi' },
    { value: 'Norwegian', label: 'Norwegian' },
    { value: 'Polish', label: 'Polish' },
    { value: 'Portuguese', label: 'Portuguese' },
    { value: 'Romanian', label: 'Romanian' },
    { value: 'Russian', label: 'Russian' },
    { value: 'Serbian', label: 'Serbian' },
    { value: 'Slovak', label: 'Slovak' },
    { value: 'Slovenian', label: 'Slovenian' },
    { value: 'Spanish', label: 'Spanish' },
    { value: 'Swahili', label: 'Swahili' },
    { value: 'Swedish', label: 'Swedish' },
    { value: 'Tamil', label: 'Tamil' },
    { value: 'Telugu', label: 'Telugu' },
    { value: 'Thai', label: 'Thai' },
    { value: 'Turkish', label: 'Turkish' },
    { value: 'Ukrainian', label: 'Ukrainian' },
    { value: 'Urdu', label: 'Urdu' },
    { value: 'Vietnamese', label: 'Vietnamese' }
  ]

  const writingStyles = [
    { value: 'Resmi', label: 'Resmi' },
    { value: 'Bersahabat', label: 'Bersahabat' }
  ]

  // Fungsi untuk mendapatkan info langganan pengguna
  const getUserSubscription = async () => {
    setLoadingSubscription(true);
    try {
      // Verifikasi sesi dengan fungsi yang lebih robust
      const session = await getActiveSession();
      if (!session?.user?.id) {
        console.log('Tidak ada user ID valid di index.js');
        setUserSubscription(null);
        setLoadingSubscription(false);
        return;
      }
      
      // Gunakan fungsi getUserSubscription yang disediakan oleh supabaseClient
      const { data, error } = await getSubscription(session.user.id);
      
      if (error) {
        console.error('Error getting user subscription:', error);
        setUserSubscription(null);
      } else if (data) {
        console.log('Data langganan ditemukan:', data.plan_name, 'dengan', data.credits_remaining, 'kredit tersisa');
        setUserSubscription(data);
      } else {
        console.log('Tidak ada data langganan yang aktif');
        setUserSubscription(null);
      }
    } catch (error) {
      console.error('Error retrieving subscription:', error);
      setUserSubscription(null);
    } finally {
      setLoadingSubscription(false);
    }
  };

  // Tambahkan useEffect untuk memuat data langganan saat komponen pertama kali dimuat
  useEffect(() => {
    getUserSubscription();
  }, []);

  // Tambahkan log untuk memastikan banner ditampilkan di return statement
  useEffect(() => {
    if (showCreditWarning) {
      console.log('showCreditWarning = true, dengan pesan:', creditWarningMessage);
    }
  }, [showCreditWarning, creditWarningMessage]);

  // useEffect untuk memeriksa dan mencoba menyimpan artikel yang backup di localStorage
  useEffect(() => {
    // Fitur ini telah dinonaktifkan karena menyebabkan masalah kinerja
    // Kode di bawah ini dikomentari agar tidak mengganggu kinerja aplikasi
    /*
    // Fungsi untuk memeriksa dan mencoba menyimpan artikel dari backup
    const checkAndSaveBackupArticle = async () => {
      try {
        // Periksa apakah ada artikel backup di localStorage
        const backupArticleJson = localStorage.getItem('article_backup');
        if (!backupArticleJson) return;
        
        console.log('Ditemukan artikel backup, mencoba menyimpan...');
        
        // Verifikasi sesi pengguna
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user?.id) {
          console.log('Tidak ada sesi pengguna, menunda penyimpanan artikel backup');
          return;
        }
        
        // Parse data artikel
        const backupArticle = JSON.parse(backupArticleJson);
        
        // Persiapkan data artikel untuk disimpan
        const articleData = {
          ...backupArticle,
          user_id: session.user.id
        };
        
        // Coba simpan artikel ke Supabase
        const { error } = await supabase
          .from('articles')
          .insert([articleData]);
        
        if (error) {
          console.error('Gagal menyimpan artikel backup:', error);
          return;
        }
        
        // Hapus backup jika berhasil disimpan
        localStorage.removeItem('article_backup');
        console.log('Artikel backup berhasil disimpan dan dihapus dari localStorage');
      } catch (error) {
        console.error('Error saat memeriksa/menyimpan artikel backup:', error);
      }
    };
    
    // Jalankan pengecekan artikel backup dengan delay
    const timeoutId = setTimeout(() => {
      checkAndSaveBackupArticle();
    }, 5000); // Beri waktu 5 detik untuk memastikan koneksi sudah stabil
    
    return () => clearTimeout(timeoutId);
    */
  }, []);

  // Tambahkan useEffect untuk mengganti semua Link menjadi hard refresh
  useEffect(() => {
    if (typeof window !== 'undefined') {
      // Fungsi untuk mengganti semua link Next.js dengan anchor yang melakukan hard refresh
      function convertAllLinksToHardRefresh() {
        // Tunggu sampai DOM telah dimuat
        if (document.readyState !== 'complete') {
          setTimeout(convertAllLinksToHardRefresh, 100);
          return;
        }
        
        // Cari semua link di halaman
        const allLinks = document.querySelectorAll('a[href]');
        
        allLinks.forEach(link => {
          // Jika link belum dimodifikasi
          if (!link.getAttribute('data-hard-refresh')) {
            const href = link.getAttribute('href');
            
            // Hanya proses link internal (bukan link eksternal atau anchor)
            if (href && href.startsWith('/') && !href.startsWith('//') && !href.startsWith('/#')) {
              // Tandai link sudah dimodifikasi
              link.setAttribute('data-hard-refresh', 'true');
              
              // Ganti handler onclick
              link.addEventListener('click', function(e) {
                e.preventDefault();
                
                // Simpan URL tujuan ke sessionStorage
                sessionStorage.setItem('nextRoute', href);
                
                // Simpan state pengguna jika perlu
                if (user && user.id) {
                  sessionStorage.setItem('preserveUser', JSON.stringify({
                    id: user.id,
                    timestamp: Date.now()
                  }));
                }
                
                // Lakukan hard refresh dengan parameter untuk mencegah cache
                window.location.href = href + (href.includes('?') ? '&' : '?') + '_refresh=' + Date.now();
              });
            }
          }
        });
      }
      
      // Jalankan saat pertama kali dan pada setiap perubahan DOM
      convertAllLinksToHardRefresh();
      
      // Setup MutationObserver untuk mendeteksi perubahan DOM
      const observer = new MutationObserver(() => {
        convertAllLinksToHardRefresh();
      });
      
      // Mulai observe perubahan DOM
      observer.observe(document.body, { 
        childList: true, 
        subtree: true 
      });
      
      return () => {
        // Bersihkan observer saat komponen unmount
        observer.disconnect();
      };
    }
  }, [user]);

  // Tambahkan fungsi handleNavigation kembali untuk anchor tag yang sudah diubah
  const handleNavigation = (e, path) => {
    e.preventDefault();
    
    // Simpan URL tujuan ke sessionStorage
    sessionStorage.setItem('nextRoute', path);
    
    // Simpan state pengguna jika perlu
    if (user && user.id) {
      sessionStorage.setItem('preserveUser', JSON.stringify({
        id: user.id,
        timestamp: Date.now()
      }));
    }
    
    // Lakukan hard refresh dengan parameter untuk mencegah cache
    window.location.href = path + (path.includes('?') ? '&' : '?') + '_refresh=' + Date.now();
  };

  return (
    <div>
      {/* Header dengan logo */}
      <header className="app-header">
        <div className="header-container">
          <div className="logo-container">
            <Link href="/">
              <img 
                src="https://ratu.ai/wp-content/uploads/2025/04/penulis-pro-ratu-logo.jpg" 
                alt="Penulis Pro Logo" 
                className="app-logo" 
              />
            </Link>
          </div>
          <div className="right-section">
            <div className="nav-links">
              <a 
                href="/" 
                onClick={(e) => handleNavigation(e, '/')} 
                className="nav-link active"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="nav-icon">
                  <path d="M12 19l7-7 3 3-7 7-3-3z"></path>
                  <path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"></path>
                  <path d="M2 2l7.586 7.586"></path>
                  <circle cx="11" cy="11" r="2"></circle>
                </svg>
                <span>Buat Artikel</span>
              </a>
              <a 
                href="/articles" 
                onClick={(e) => handleNavigation(e, '/articles')} 
                className="nav-link"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="nav-icon">
                  <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
                  <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
                </svg>
                <span>Artikel Tersimpan</span>
              </a>
              <a 
                href="/humanize" 
                onClick={(e) => handleNavigation(e, '/humanize')} 
                className="nav-link"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="nav-icon">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="9" y1="8" x2="15" y2="8"></line>
                  <line x1="9" y1="12" x2="15" y2="12"></line>
                  <line x1="9" y1="16" x2="13" y2="16"></line>
                  <line x1="17" y1="17" x2="19" y2="19"></line>
                  <line x1="19" y1="17" x2="17" y2="19"></line>
                </svg>
                <span>Humanize</span>
              </a>
            </div>
            
            <ProfileButton />
          </div>
        </div>
      </header>

      <div className="container">
        {/* Loading Animation Overlay */}
        <div className="article-loading-overlay">
          <div className="article-loading-content">
            <div className="article-loading-icon">
              <div className="article-loading-pulse"></div>
              <div className="article-loading-circle"></div>
              <div className="article-loading-circle"></div>
              <div className="article-loading-circle"></div>
            </div>
            <div className="article-loading-title">Sedang Membuat Artikel</div>
            <div className="article-loading-text">Mohon tunggu sebentar, kami sedang memproses artikel Anda dengan kualitas terbaik dan dengan berbagai sumber referensi terbaik.</div>
            <div className="article-loading-progress">
              <div className="article-loading-progress-bar"></div>
            </div>
            <div className="article-loading-dots">
              <span></span>
              <span></span>
              <span></span>
            </div>
            <div className="article-loading-status">Menyusun konten...</div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="form-no-bg">
          <label htmlFor="text">Judul atau Topik:</label>
          <textarea
            id="text"
            rows="1"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Masukkan judul atau topik di sini..."
            required
            className="single-line-textarea"
          />
          
          <label htmlFor="articleTypeInput">Jenis Artikel:</label>
          <div className="custom-dropdown" ref={typeDropdownRef}>
            <div 
              className={`dropdown-selected ${typeDropdownOpen ? 'active' : ''}`}
              onClick={() => setTypeDropdownOpen(!typeDropdownOpen)}
            >
              {articleTypes.find(type => type.value === articleType)?.label}
              <span className="dropdown-arrow"></span>
            </div>
            {typeDropdownOpen && (
              <div className="dropdown-options">
                {articleTypes.map(type => (
                  <div 
                    key={type.value} 
                    className={`dropdown-option ${type.value === articleType ? 'selected' : ''}`}
                    onClick={() => {
                      setArticleType(type.value)
                      setTypeDropdownOpen(false)
                    }}
                  >
                    {type.label}
                  </div>
                ))}
              </div>
            )}
            <input type="hidden" name="articleType" id="articleTypeInput" value={articleType} />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="languageInput">Pilih bahasa:</label>
              <div className="custom-dropdown" ref={languageDropdownRef}>
                <div 
                  className={`dropdown-selected ${languageDropdownOpen ? 'active' : ''}`}
                  onClick={() => setLanguageDropdownOpen(!languageDropdownOpen)}
                  id="language"
                >
                  {languages.find(lang => lang.value === language)?.label}
                  <span className="dropdown-arrow"></span>
                </div>
                {languageDropdownOpen && (
                  <div className="dropdown-options with-search">
                    <div className="dropdown-search-container">
                      <input
                        type="text"
                        className="dropdown-search"
                        placeholder="Cari bahasa..."
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => {
                          const searchInput = e.target.value.toLowerCase();
                          const options = document.querySelectorAll('.lang-option');
                          
                          options.forEach(option => {
                            const text = option.textContent.toLowerCase();
                            option.style.display = text.includes(searchInput) ? '' : 'none';
                          });
                        }}
                      />
                    </div>
                    <div className="dropdown-options-scrollable">
                      {languages.map(lang => (
                        <div 
                          key={lang.value} 
                          className={`dropdown-option lang-option ${lang.value === language ? 'selected' : ''}`}
                          onClick={() => {
                            setLanguage(lang.value)
                            setLanguageDropdownOpen(false)
                          }}
                        >
                          {lang.label}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <input type="hidden" name="language" id="languageInput" value={language} />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="writingStyleInput">Pilih gaya penulisan:</label>
              <div className="custom-dropdown" ref={styleDropdownRef}>
                <div 
                  className={`dropdown-selected ${styleDropdownOpen ? 'active' : ''}`}
                  onClick={() => setStyleDropdownOpen(!styleDropdownOpen)}
                  id="writingStyle"
                >
                  {writingStyles.find(style => style.value === writingStyle)?.label}
                  <span className="dropdown-arrow"></span>
                </div>
                {styleDropdownOpen && (
                  <div className="dropdown-options">
                    {writingStyles.map(style => (
                      <div 
                        key={style.value} 
                        className={`dropdown-option ${style.value === writingStyle ? 'selected' : ''}`}
                        onClick={() => {
                          setWritingStyle(style.value)
                          setStyleDropdownOpen(false)
                        }}
                      >
                        {style.label}
                      </div>
                    ))}
                  </div>
                )}
                <input type="hidden" name="writingStyle" id="writingStyleInput" value={writingStyle} />
              </div>
            </div>
          </div>
          
          {/* Dropdown Sumber Referensi - hanya tersedia jika bahasa Indonesia dan bukan artikel Cerita */}
          {articleType !== 'Cerita' && language === 'Bahasa Indonesia' && (
            <div className="form-group">
              <label htmlFor="referenceSourceInput">Sumber referensi:</label>
              <div className="custom-dropdown" ref={referenceSourceDropdownRef}>
                <div 
                  className={`dropdown-selected ${referenceSourceDropdownOpen ? 'active' : ''}`}
                  onClick={() => setReferenceSourceDropdownOpen(!referenceSourceDropdownOpen)}
                  id="referenceSource"
                >
                  {referenceSource === 'Indonesia' ? 'Indonesia' : 'Luar negeri'}
                  <span className="dropdown-arrow"></span>
                </div>
                {referenceSourceDropdownOpen && (
                  <div className="dropdown-options">
                    <div 
                      className={`dropdown-option ${referenceSource === 'Indonesia' ? 'selected' : ''}`}
                      onClick={() => {
                        setReferenceSource('Indonesia')
                        console.log('Memilih sumber referensi: Indonesia')
                        setReferenceSourceDropdownOpen(false)
                      }}
                    >
                      Indonesia
                    </div>
                    <div 
                      className={`dropdown-option ${referenceSource === 'International' ? 'selected' : ''}`}
                      onClick={() => {
                        setReferenceSource('International')
                        console.log('Memilih sumber referensi: International')
                        setReferenceSourceDropdownOpen(false)
                      }}
                    >
                      Luar negeri
                    </div>
                  </div>
                )}
                <input type="hidden" name="referenceSource" id="referenceSourceInput" value={referenceSource} />
              </div>
            </div>
          )}
          
          {/* Keterangan sumber referensi untuk bahasa selain Indonesia */}
          {articleType !== 'Cerita' && language !== 'Bahasa Indonesia' && (
            <div className="form-group">
              <label htmlFor="referenceSourceInput">Sumber referensi:</label>
              <div className="info-text">
                <span>Luar negeri <small>(otomatis untuk bahasa non-Indonesia)</small></span>
              </div>
              <input type="hidden" name="referenceSource" id="referenceSourceInput" value="International" />
            </div>
          )}
          
          <button type="submit" disabled={loading}>
            {loading ? (streaming ? 'Membuat artikel...' : 'Sedang memproses...') : 'Buat Artikel'}
          </button>
        </form>
        {result && (
          <div className="result">
            <div className="article-detail">
              <div className="article-detail-header">
                <h1 className="article-detail-title">{text}</h1>
                <div className="article-detail-meta">
                  <div className="article-detail-tags">
                    <span className="article-type">{articleType}</span>
                    <span className="article-language">{language}</span>
                    <span className="article-style">{writingStyle}</span>
                    {articleType !== 'Cerita' && (
                      <span className="article-source article-reference-source">
                        <span className="source-label">Sumber:</span> {referenceSource === 'Indonesia' ? 'Indonesia' : 'Luar Negeri'}
                      </span>
                    )}
                  </div>
                  <div className="article-detail-date">
                    Dibuat pada: {formatTimestamp(timestamp)}
                  </div>
                </div>
                {humanized && (
                  <div className="humanized-badge-container">
                    <div className="humanized-badge">
                      <span className="humanized-icon">✓</span>
                      <span className="humanized-text">AI-Undetectable</span>
                      <div className="humanized-tooltip">Artikel ini telah dioptimalkan agar tidak terdeteksi sebagai konten AI oleh detector seperti GPTZero, Winston AI, dan lainnya.</div>
                    </div>
                  </div>
                )}
              </div>
              <div className="article-detail-content" ref={resultRef}>
                {renderArticleContent()}
              </div>
            </div>
            <div className="result-footer">
              <div className="action-buttons">
                <button 
                  onClick={handleCopy} 
                  className={`copy-button ${copySuccess ? 'success' : ''}`}
                >
                  {copySuccess ? '✓ Tersalin!' : 'Salin Artikel'}
                </button>
                <button 
                  onClick={humanizeText}
                  className={`humanize-button ${humanizing ? 'loading' : ''} ${humanized ? 'success' : ''}`}
                  disabled={humanizing || humanized}
                >
                  {humanizing ? 'Memanusiakan...' : humanized ? '✓ Sudah Dimanusiakan' : 'Humanize'}
                </button>
                <button 
                  onClick={saveArticleToSupabase}
                  className={`save-button ${saveStatus ? saveStatus : ''}`}
                  disabled={saveStatus === 'saving' || saveStatus === 'success'}
                >
                  {saveStatus === 'saving' ? 'Menyimpan...' : 
                   saveStatus === 'success' ? '✓ Tersimpan!' :
                   saveStatus === 'error' ? 'Gagal Menyimpan' :
                   'Simpan Artikel'}
                </button>
                <button 
                  onClick={handleCreateNew}
                  className="new-button"
                >
                  Buat Baru
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Tambahkan CreditWarningBanner dengan flag jenis pesan yang tepat */}
        {showCreditWarning && (
          console.log('Rendering CreditWarningBanner di JSX dengan pesan:', creditWarningMessage),
          <CreditWarningBanner 
            message={creditWarningMessage}
            isConnectionError={false} // SELALU tampilkan sebagai pesan kredit habis, bukan koneksi
            onClose={() => {
              console.log('onClose callback dipanggil');
              setShowCreditWarning(false);
            }}
          />
        )}
      </div>
    </div>
  )
}
